import { isRejectedWithValue } from '@reduxjs/toolkit';
import type { Middleware } from '@reduxjs/toolkit';
import { message } from 'antd';
import { isRateCardNotFoundError } from '@/pages/MediaPlanDetails/utils/rateCardLookup';

const RATE_CARD_LOOKUP_ENDPOINTS = new Set([
  'getRateCardOptions',
  'getChannelMetrics'
]);

/**
 * PandaDoc endpoints classify their own errors (session expired vs. upstream
 * integration failure vs. request error) and show a safe message locally — see
 * `classifyPandaDocError` / `pandaDocErrorMessage` in `@/utils/pandaDoc`. Letting this
 * middleware also toast the raw backend message would leak things like
 * `Request failed with status code 401` (an AxiosError proxied from PandaDoc) straight
 * to the user.
 */
const PANDA_DOC_ENDPOINTS = new Set([
  'listTemplates',
  'getTemplateDetails',
  'listDocuments',
  'createDocument',
  'raiseIo',
  'getDocument',
  'downloadDocument',
  'sendDocument',
  'deleteDocument',
  /* The BoldSign-era IO and template endpoints whose screens report their own
     failure. Ones that do not (template delete and preview, the version and
     history reads) are left to the global toast so they are never silent. */
  'revokeDocument',
  'updatePoNumber',
  'updatePrismaStatus',
  'uploadPrismaFile',
  'getPrismaFileLink',
  'saveIoTemplate',
  'resetIoTemplate',
  'createIoTemplate'
]);

/**
 * Log a warning and show a toast!
 */
export const errorHandler: Middleware = () => (next) => (action) => {
  const errorMessage = action.payload?.data?.message;

  if (isRejectedWithValue(action) && errorMessage) {
    const meta = (action as { meta?: { arg?: { endpointName?: string; originalArgs?: unknown } } }).meta;
    const endpointName = meta?.arg?.endpointName;
    /** Mutations show errors in form handlers; avoid duplicate toasts. */
    const skipGlobalToast =
      ['updateChannel', 'createChannel', 'deleteChannel'].includes(endpointName ?? '') ||
      PANDA_DOC_ENDPOINTS.has(endpointName ?? '');

    if (!skipGlobalToast) {
      if (isRateCardNotFoundError(errorMessage) && RATE_CARD_LOOKUP_ENDPOINTS.has(endpointName ?? '')) {
        // Wizard / list guards should prevent this; suppress noisy raw backend text if it still slips through.
        return next(action);
      }
      message.error(errorMessage);
    }
  }

  return next(action);
};
